import React, { useEffect, useRef } from "react";
import "./About.css";
import { Award, Users, Server, Cpu } from "lucide-react";
import CertificationBadges from "../components/CertificationBadges.js";
import SEO from "../components/SEO.js";

const About = () => {
  const cardRefs = useRef([]);
  const nexusRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in-view');
        }
      });
    }, { threshold: 0.1 });

    const hiddenElements = document.querySelectorAll('.animate-on-scroll');
    hiddenElements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const handleMouseMove = (e, index) => {
    let card;
    if (index === 'nexus') {
      card = nexusRef.current;
    } else {
      card = cardRefs.current[index];
    }

    if (!card) return;

    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const rotateX = (y - centerY) / 60;
    const rotateY = (centerX - x) / 60;

    card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1, 1, 1)`;

    const overlay = card.querySelector('.portal-card-overlay, .nexus-background-mesh');
    if (overlay) {
      overlay.style.background = `radial-gradient(circle at ${x}px ${y}px, rgba(255,102,0,0.15) 0%, transparent 80%)`;
    }
  };

  const handleMouseLeave = (index) => {
    let card;
    if (index === 'nexus') {
      card = nexusRef.current;
    } else {
      card = cardRefs.current[index];
    }

    if (!card) return;
    card.style.transform = `perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)`;
    const overlay = card.querySelector('.portal-card-overlay, .nexus-background-mesh');
    if (overlay) {
      overlay.style.background = ``;
    }
  };

  const coreValues = [
    {
      title: "Innovation",
      subtitle: "INNOVATION THROUGH IDEAS",
      icon: "/images/about-001.png",
      desc: "Innovation is our identity. We believe that true transformation happens when fresh ideas are encouraged, explored, and implemented with precision. It is a mindset that influences every process and partnership we create.",
      points: ["R&D focused approach", "Future-ready solutions", "Continuous experimentation"]
    },
    {
      title: "Teamwork",
      subtitle: "TOGETHER EVERYONE ACHIEVES MORE",
      icon: "/images/about-002.png",
      desc: "We cultivate a workplace culture that celebrates collaboration, openness, and shared success. Each team member contributes their expertise toward our common goals.",
      points: ["56 skilled professionals", "Collaborative culture", "Shared commitment to excellence"]
    }
  ];

  const highlights = [
    { icon: <Cpu />, text: "Examination Automation" },
    { icon: <Server />, text: "ERP Solutions" },
    { icon: <Award />, text: "Custom Software Development" },
    { icon: <Users />, text: "Client-Centric Approach" }
  ];

  return (
    <div className="modern-about-wrapper">
      <SEO
        title="About Us — D Base Solutions Pvt Ltd"
        description="Learn about D Base Solutions (est. 2011), our leadership, values, innovations, and expertise across EdTech, ERP, and university software engineering in India."
        keywords="About D Base Solutions, DBS company background, software engineering Eluru, EdTech company Andhra Pradesh, university IT solutions"
        canonical="https://dbasesolutions.in/about"
        ogImage="/og-image.png"
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "About Us", url: "/about" }
        ]}
      />
      {/* Dynamic Background */}
      <div className="bg-shapes">
        <div className="bg-shape shape-1"></div>
        <div className="bg-shape shape-2"></div>
        <div className="bg-shape shape-3"></div>
      </div>

      {/* Decorative Blobs */}
      <div className="about-blob about-blob-1"></div>
      <div className="about-blob about-blob-2"></div>
      <div className="about-blob about-blob-3"></div>

      {/* Hero Section */}
      <section className="modern-hero">
        <div className="container">
          <div className="hero-content animate-on-scroll">
            <span className="hero-overline">ESTABLISHED 2011</span>
            <h1 className="hero-headline">
              Redefining <span className="text-gradient">IT Solutions</span> <br />
              for a Digital India.
            </h1>
            <p className="hero-subline">
              D Base Solutions Private Limited (DBS) is a trusted technology partner delivering specialized services
              in examination automation, ERP, and custom software development.
            </p>
            <div className="hero-highlights">
              {highlights.map((h, i) => (
                <div key={i} className="highlight-tag">
                  <span className="h-icon">{h.icon}</span>
                  <span className="h-text">{h.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CertificationBadges />

      {/* Our Story Section */}
      <section className="story-section">
        <div className="container">
          <div className="story-grid">
            <div className="story-image animate-on-scroll">
              <div className="image-stack">
                <div className="stack-img main-img">
                  <img src="/images/about-01.png" alt="DBS Team" />
                </div>
                <div className="stack-img deco-img"></div>
                <div className="experience-badge">
                  <span className="years">14+</span>
                  <span className="label">Years of Innovation</span>
                </div>
              </div>
            </div>
            <div className="story-text animate-on-scroll">
              <h2 className="section-title">Our <span className="text-gradient">Journey</span></h2>
              <p>
                Rooted in a passion for technology, DBS has grown from a small technology firm in Eluru
                into a leading IT solutions provider across India. We embarked on a mission to solve
                real-world problems through practical, scalable, and cost-effective digital solutions.
              </p>
              <p>
                Over the years, we have evolved into a trusted technology partner known for delivering
                excellence in examination automation and enterprise resource planning.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values Section */}
      <section className="vibrant-core-values-wrapper">
        <div className="container">
          <div className="vibrant-header-container animate-on-scroll">
            <h2 className="vibrant-main-title">Core <span className="vibrant-gradient-text">Values</span></h2>
            <p className="vibrant-sub-desc">The principles that drive our innovation and excellence.</p>
          </div>
          <div className="staggered-premium-grid">
            {coreValues.map((value, index) => (
              <div key={index} className={`elite-value-card card-variant-${index} animate-on-scroll`}>
                <div className="elite-card-inner">
                  <div className="elite-icon-box">
                    <img src={value.icon} alt={value.title} className="elite-floating-img" />
                    <div className="elite-icon-glow"></div>
                  </div>
                  <div className="elite-text-content">
                    <span className="elite-overline">{value.subtitle}</span>
                    <h3 className="elite-title">{value.title}</h3>
                    <p className="elite-desc">{value.desc}</p>
                    <ul className="elite-feature-list">
                      {value.points.map((p, i) => (
                        <li key={i} className="elite-feature-item">
                          <span className="elite-check-icon">✦</span>
                          {p}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div className="elite-card-bg-effect"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="cosmic-mission-vision-zone">
        <div className="container">
          <div className="dual-portal-layout">
            <div
              ref={el => cardRefs.current[0] = el}
              className="ethereal-mv-card portal-mission animate-on-scroll"
              onMouseMove={(e) => handleMouseMove(e, 0)}
              onMouseLeave={() => handleMouseLeave(0)}
            >
              <div className="portal-visual-wrap">
                <div className="portal-icon-container">
                  <img src="/images/about-004.png" alt="Mission" className="portal-3d-asset" />
                </div>
                <div className="portal-ring"></div>
              </div>
              <div className="portal-details">
                <h2 className="portal-heading">Mission</h2>
                <div className="portal-accent-line"></div>
                <p className="portal-intro">To emerge as the most preferred technological partner by offering:</p>
                <div className="portal-list-container">
                  <ul className="portal-bullet-list">
                    <li>High-quality, cost-effective, and secure software engineering services</li>
                    <li>Advanced energy-efficient IT systems</li>
                    <li>Agile and adaptable solutions for Educational Institutions, Public Organizations, and Private Enterprises</li>
                  </ul>
                </div>
                <p className="portal-footer-text">Our goal is to build long-term value for our clients by aligning technology with their strategic goals.</p>
              </div>
              <div className="portal-card-overlay"></div>
            </div>

            <div
              ref={el => cardRefs.current[1] = el}
              className="ethereal-mv-card portal-vision animate-on-scroll"
              onMouseMove={(e) => handleMouseMove(e, 1)}
              onMouseLeave={() => handleMouseLeave(1)}
            >
              <div className="portal-visual-wrap">
                <div className="portal-icon-container">
                  <img src="/images/about-003.png" alt="Vision" className="portal-3d-asset" />
                </div>
                <div className="portal-ring"></div>
              </div>
              <div className="portal-details">
                <h2 className="portal-heading">Vision</h2>
                <div className="portal-accent-line v-accent"></div>
                <p className="portal-intro dark-text">To become a leading contributor to system-level technological innovation, developing end-to-end digital solutions that transform operations in:</p>
                <div className="portal-list-container">
                  <ul className="portal-bullet-list dark-list">
                    <li>Educational Institutions</li>
                    <li>Financial Services</li>
                    <li>Marketing and Communications</li>
                    <li>Industrial and Public Sectors</li>
                  </ul>
                </div>
                <p className="portal-footer-text dark-text">We aim to be recognized not only for our technical prowess but also for the real-world impact our solutions deliver.</p>
              </div>
              <div className="portal-card-overlay light"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Customer Service */}
      <section className="nexus-service-experience">
        <div className="container">
          <div
            ref={nexusRef}
            className="infinity-banner-creative animate-on-scroll"
            onMouseMove={(e) => handleMouseMove(e, 'nexus')}
            onMouseLeave={() => handleMouseLeave('nexus')}
          >
            <div className="nexus-visual-anchor">
              <div className="nexus-3d-sphere">
                <img src="/images/about-005.png" alt="Customer Service" className="nexus-floating-asset" />
              </div>
              <div className="nexus-particle-field"></div>
            </div>
            <div className="nexus-text-block">
              <h2 className="nexus-main-heading">Customer <span className="nexus-highlight">Service</span></h2>
              <div className="nexus-underline"></div>
              <p className="nexus-description">We believe in not just meeting but exceeding customer expectations. We strive toward consistency, detail, and building lasting relationships with our clients.</p>
              <div className="nexus-action-area">
                <button className="mega-cta-vibe">
                  <span>Connect with Experts</span>
                </button>
              </div>
            </div>
            <div className="nexus-background-mesh"></div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
